'use client'

import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { useSearchParam } from '@/lib/next/useSearchParam'
import { Button } from '@/ui/buttons/button/Button.client'
import { Icon } from '@/ui/icons/icon/Icon'
import { Container } from '@/ui/layouts/container/Container'
import { Skin } from '@/ui/skin/Skin'
import type { ProductListFilterFragment } from '@gql-generated/graphql'
import { useSetAtom } from 'jotai'
import { useEffect, useRef } from 'react'
import {
  productListFiltersMenuRefAtom,
  productListFiltersMobileItemOpen,
} from '../../services/productList.store'
import { createProductsFiltersDrawerOpenIntent } from '../../services/productListFilters.intents'

const PRODUCT_FILTERS_SHOWED_LIMIT = 3

type ProductListFiltersMobileMenuProps = {
  productFilters: ProductListFilterFragment[]
  countSubcategoriesFilters?: number
  countCategoriesFilters?: number
  hasActiveFilters?: boolean
}

export function ProductListFiltersMobileMenu({
  productFilters,
  countSubcategoriesFilters = 0,
  countCategoriesFilters = 0,
  hasActiveFilters,
}: ProductListFiltersMobileMenuProps) {
  const { t } = useTranslation(['generic', 'productListFilters', 'productListFiltersProduct'])
  const setFilterNameActive = useSetAtom(productListFiltersMobileItemOpen)
  const setMenuRef = useSetAtom(productListFiltersMenuRefAtom)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const buttonsClassName = 'shrink-0'
  const filter = useSearchParam('filter')

  useEffect(() => {
    setMenuRef(scrollContainerRef)
  }, [setMenuRef])

  return (
    <div className="desktop:z-auto z-header-overlay sticky-mobile-to-header">
      <Skin tint="body" className="pt-2 desktop:pt-0">
        <Container>
          <div
            ref={scrollContainerRef}
            className="flex flex-row flex-nowrap gap-1 overflow-auto pb-2 scrollbar-none desktop:hidden"
          >
            <Button
              size="md"
              variant={!hasActiveFilters ? 'secondary' : 'primary'}
              aria-label={t('productListFilters:openFilters')}
              iconLeft={!hasActiveFilters ? <Icon.ListFilter /> : <Icon.ListFilterPlus />}
              onPress={() => {
                setFilterNameActive(true)
              }}
              className={buttonsClassName}
              intent={createProductsFiltersDrawerOpenIntent({
                requestFilter: filter || '',
                selectedFilterGroup: undefined,
              })}
            >
              {!hasActiveFilters
                ? t('productListFilters:filters')
                : t('productListFilters:activatedFilters')}
            </Button>
            {!!countSubcategoriesFilters && (
              <Button
                size="md"
                variant="secondary"
                onPress={() => setFilterNameActive('subcategories')}
                className={buttonsClassName}
                intent={createProductsFiltersDrawerOpenIntent({
                  requestFilter: filter || '',
                  selectedFilterGroup: 'subcategories',
                })}
              >
                {t('productListFilters:subcategories', {
                  count: countSubcategoriesFilters,
                })}
              </Button>
            )}
            {!!countCategoriesFilters && (
              <Button
                size="md"
                variant="secondary"
                onPress={() => setFilterNameActive('categories')}
                className={buttonsClassName}
                intent={createProductsFiltersDrawerOpenIntent({
                  requestFilter: filter || '',
                  selectedFilterGroup: 'categories',
                })}
              >
                {t('productListFilters:categories', { count: countCategoriesFilters })}
              </Button>
            )}

            {productFilters?.slice(0, PRODUCT_FILTERS_SHOWED_LIMIT).map((productFilter) => (
              <Button
                size="md"
                key={productFilter.title}
                variant="secondary"
                onPress={() => setFilterNameActive(productFilter.title)}
                className={buttonsClassName}
                intent={createProductsFiltersDrawerOpenIntent({
                  requestFilter: filter || '',
                  selectedFilterGroup: productFilter.title,
                })}
              >
                {productFilter.title === t('productListFiltersProduct:sections.brand.title') &&
                t('productListFiltersProduct:sections.brand.accordionTitle')
                  ? t('productListFiltersProduct:sections.brand.accordionTitle')
                  : productFilter.title}
              </Button>
            ))}

            {productFilters && productFilters.length >= PRODUCT_FILTERS_SHOWED_LIMIT + 1 && (
              <Button
                size="md"
                variant="secondary"
                onPress={() => setFilterNameActive(true)}
                className={buttonsClassName}
                intent={createProductsFiltersDrawerOpenIntent({
                  requestFilter: filter || '',
                  selectedFilterGroup: undefined,
                })}
              >
                {t('productListFilters:moreFilters')}
              </Button>
            )}
          </div>
        </Container>
      </Skin>
    </div>
  )
}
